import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { addSeconds, differenceInMilliseconds } from 'date-fns';

import { PopupNames } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';
import { PopupTimerStorage } from 'types/interfaces/Popups';

import { isMonetizationPage } from 'helpers/route';
import {
  getSessionStorageItem,
  setSessionStorageItem,
} from 'helpers/sessionStorage';
import { useVipDiscountActivationMutation } from 'hooks/mutations/useVipDiscountActivationMutation';
import { usePopup } from 'hooks/usePopup';
import { getAuthenticated, getIsBuyer, getIsVip } from 'store/auth/selectors';
import { isPromoteVipPopupEnabledSelector } from 'store/common/selectors';
import {
  setVipDiscountAvailable,
  setVipPackages,
} from 'store/payment/paymentSlice';
import {
  getVipDiscountAvailable,
  getVipPaymentSuccess,
} from 'store/payment/selectors';

const PROMOTE_POPUP_STORAGE_KEY = 'PROMOTE_VIP_POPUP';

export const TIMERS_DELAY = [30, 90, 180];

export const getDataFromStorage = (): PopupTimerStorage[] | null => {
  const data = getSessionStorageItem(PROMOTE_POPUP_STORAGE_KEY);

  return data ? JSON.parse(data) : null;
};

export const setDataToStorage = (data: PopupTimerStorage[] | null) => {
  setSessionStorageItem(PROMOTE_POPUP_STORAGE_KEY, JSON.stringify(data));
};

export const usePromoteVipPopup = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const isUserBuyer = useSelector(getIsBuyer);
  const isUserVip = useSelector(getIsVip);
  const isAuthenticated = useSelector(getAuthenticated);
  const isPromoteVipPopupEnabled = useSelector(
    isPromoteVipPopupEnabledSelector
  );
  const isVipDiscountAvailable = useSelector(getVipDiscountAvailable);
  const isVipPaymentSuccess = useSelector(getVipPaymentSuccess);

  const [isInited, setIsInited] = useState(false);

  const localTimer = useRef<NodeJS.Timeout | null>(null);
  const timer = useRef<NodeJS.Timeout[]>([]);

  const { handleOpenASAP, handleAction, handleClose, handleRemoveFromQueue } =
    usePopup(PopupNames.PromoteVip);

  const vipDiscountActivationMutation = useVipDiscountActivationMutation({
    onSuccess: (data) => {
      if (data.packages.length) {
        dispatch(setVipPackages(data.packages));
      }

      dispatch(setVipDiscountAvailable(false));
    },
  });

  handleAction.current = (action: { type: string }) => {
    if (action.type === 'close') {
      handleClose();
    }
  };

  const handleOpenPopup = useCallback(
    (id: number) => {
      const storageData = getDataFromStorage();

      if (storageData) {
        setDataToStorage(
          storageData.map((popup) => {
            return popup.id === id ? { ...popup, isActivated: true } : popup;
          })
        );

        handleOpenASAP(null);
      }
    },
    [handleOpenASAP]
  );

  useEffect(() => {
    if (isAuthenticated && isUserBuyer && !isUserVip) {
      const storageData = getDataFromStorage();

      if (storageData) {
        setIsInited(true);
      } else if (!isMonetizationPage(location.pathname)) {
        setDataToStorage(
          TIMERS_DELAY.map((delay) => {
            return {
              id: delay,
              isActivated: false,
              delay: String(addSeconds(new Date(), delay)),
            };
          })
        );
        setIsInited(true);
      }
    }
  }, [
    isAuthenticated,
    isPromoteVipPopupEnabled,
    isUserBuyer,
    isUserVip,
    location.pathname,
  ]);

  useEffect(() => {
    const storageData = getDataFromStorage();
    const timerValue = timer.current;

    if (isInited && storageData) {
      storageData.forEach((data) => {
        if (
          !data.isActivated &&
          !MirrorService.isSimpleDates &&
          !MirrorService.isGoogleCertificationsGroup &&
          !isUserVip
        ) {
          timerValue.push(
            setTimeout(() => {
              handleOpenPopup(data.id);
            }, differenceInMilliseconds(new Date(data.delay), new Date()))
          );
        }
      });
    }

    return () => {
      timerValue.forEach((time) => {
        clearTimeout(time);
      });
    };
  }, [handleOpenPopup, isInited, isUserVip]);

  useEffect(() => {
    const storageData = getDataFromStorage();
    if (!isAuthenticated && !storageData) {
      handleRemoveFromQueue();
      setDataToStorage(null);
    }
  }, []);

  useEffect(() => {
    if (isVipPaymentSuccess || isUserVip) {
      timer.current.forEach((time) => {
        clearTimeout(time);
      });
      handleRemoveFromQueue();
      setDataToStorage(null);
    }
  }, [isVipPaymentSuccess, isUserVip]);

  useEffect(() => {
    if (
      isAuthenticated &&
      isVipDiscountAvailable &&
      isPromoteVipPopupEnabled &&
      isUserBuyer &&
      !isUserVip
    ) {
      localTimer.current = setTimeout(() => {
        vipDiscountActivationMutation.mutate();
      }, 60 * 1000 * 2);
    }

    return () => {
      if (localTimer.current) {
        clearTimeout(localTimer.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    isPromoteVipPopupEnabled,
    isUserBuyer,
    isUserVip,
    isVipDiscountAvailable,
  ]);
};
